import React from 'react'
import './style/accounts.scss'
import { Link } from 'react-router-dom'
import { Button, FilterBar, PageHeader, StatusBadge, StatusBadgeProps } from '@pushly/aqe/lib/components'
import type { ActiveFilter, ButtonAction, FilterDefinition } from '@pushly/aqe/lib/components'
import { classnames } from '@halo/_utils/classnames'
import { observer } from 'mobx-react'
import {
    Loading3QuartersOutlined,
    WarningOutlined,
    EyeOutlined,
    QuestionCircleOutlined,
} from '@ant-design/icons'
import { Table, Input, Tag, Modal, Form, FormInstance, Select, Flex } from 'antd'
import { AppService } from '@halo/services/app.service'
import { Container } from 'typescript-ioc/es5'
import { AccountService } from '@halo/services/account.service'
import { AccountDto } from '@halo/dtos/account.dto'
import { ColumnProps, TablePaginationConfig } from 'antd/es/table'
import * as moment from 'moment-timezone'
import { TableRowEntityDisplay } from '@halo/components/table-row-entity-display/table-row-entity-display'
import { simpleNotification } from '@halo/_utils/notifications'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { FilterValue, SorterResult, TableCurrentDataSource } from 'antd/es/table/interface'
import { RuleObject } from 'antd/es/form'
import { CurrentUserCan } from '@halo/stores/ability.state'
import { AbilityAction } from '@halo/enums/ability-action.enum'
import { SubjectEntity } from '@halo/enums/ability-entity.enum'
import { AppState } from '@halo/stores/app.state'
import SwSimpleLinkPagination from '@halo/components/sw-simple-link-pagination/sw-simple-link-pagination'
import { IApiResponsePaginationLinks } from '@halo/interfaces/api-response-pagination-links'
import { StatusType } from '@pushly/aqe/lib/enums'
import deepEqual from 'react-fast-compare'
import { fetchWhiteLabels } from '@halo/api/white-labels.api'
import WhiteLabelModel from '@halo/models/white-label.model'

export const ORGANIZATIONS_ROUTE = '/organizations'

// Org type filter values — used as a multi-select.
// When all three are selected (or none), no filter is applied (returns all orgs).
// Default: ['standard'] — hides demo and template orgs from the default view.
type OrgTypeFilter = 'standard' | 'demo' | 'demo-template'

class AccountTableDataDto extends AccountDto {
    public static fromAccountDto(fields: Partial<AccountTableDataDto>): AccountTableDataDto {
        const dto = new AccountTableDataDto()
        Object.assign(dto, fields, {
            key: fields.id,
        })
        return dto
    }

    public readonly key: number
}

interface IColumnProps extends ColumnProps<AccountTableDataDto> {
    dataKey?: string
}

interface IAccounts {}

interface IAccountsState {
    loading: boolean
    accountsLoaded: boolean
    allAccounts: any[]
    visibleAccounts: any[]
    accountsMeta: any
    sortedInfo: any
    page: number
    pageSize: number
    query?: string
    paginationLinks?: IApiResponsePaginationLinks
    showNewAccountPrompt: boolean
    showNewDemoOrgPrompt: boolean
    showNewDemoOrgLoading: boolean
    showNewDemoTemplateOrgPrompt: boolean
    showNewDemoTemplateOrgLoading: boolean
    statusFilter: string[]
    orgTypeFilter: OrgTypeFilter[]
    whiteLabels?: WhiteLabelModel[]
}

@observer
export class Accounts extends React.Component<IAccounts, IAccountsState> {
    protected appState: AppState
    protected appService: AppService
    protected orgService: AccountService

    protected newAccountFormRef = React.createRef<FormInstance>()
    protected newDemoOrgFormRef = React.createRef<FormInstance>()
    protected newDemoTemplateOrgFormRef = React.createRef<FormInstance>()

    public constructor(props: IAccounts) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.orgService = Container.get(AccountService)

        this.state = {
            page: 1,
            pageSize: 15,
            loading: true,
            accountsLoaded: false,
            allAccounts: [],
            visibleAccounts: [],
            accountsMeta: {},
            sortedInfo: {
                order: 'ascend',
                columnKey: 'name',
            },
            showNewAccountPrompt: false,
            showNewDemoOrgPrompt: false,
            showNewDemoOrgLoading: false,
            showNewDemoTemplateOrgPrompt: false,
            showNewDemoTemplateOrgLoading: false,
            statusFilter: [StatusType[StatusType.ACTIVE]],
            orgTypeFilter: ['standard'],
        }
    }

    public componentDidMount(): void {
        this.ensureAccountsForList()
        this.fetchWhitelabels()
    }

    public async ensureAccountsForList() {
        await this.fetchAccounts()
    }

    public render() {
        const { currentUser } = this.appState
        const isInternalUser = currentUser?.isInternalUser ?? false

        // "New Organization" actions — demo options only for internal users
        const newOrgActions: ButtonAction[] | undefined = isInternalUser
            ? [
                  { key: 'new-demo-org', text: 'New Demo Organization', onClick: this.showNewDemoOrgPrompt },
                  {
                      key: 'new-demo-template-org',
                      text: 'New Demo Template Organization',
                      onClick: this.showNewDemoTemplateOrgPrompt,
                  },
              ]
            : undefined

        const filterDefinitions: FilterDefinition[] = [
            {
                key: 'status',
                label: 'Status',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: StatusType[StatusType.ACTIVE], label: 'Active' },
                    { value: StatusType[StatusType.DISABLED], label: 'Disabled' },
                ],
            },
            {
                key: 'type',
                label: 'Type',
                type: 'multi-select',
                showOperatorSelect: false,
                showSearch: false,
                selectOptions: [
                    { value: 'standard', label: 'Standard' },
                    { value: 'demo', label: 'Demo' },
                    { value: 'demo-template', label: 'Demo Template' },
                ],
            },
        ]

        const getFilterBarValue = (): ActiveFilter[] => {
            const active: ActiveFilter[] = []
            if (this.state.statusFilter.length > 0) {
                active.push({ key: 'status', operator: 'in', value: this.state.statusFilter })
            }
            if (this.state.orgTypeFilter.length > 0) {
                active.push({ key: 'type', operator: 'in', value: this.state.orgTypeFilter })
            }
            return active
        }

        const handleFilterBarApply = (filters: ActiveFilter[]) => {
            const nextStatus = (filters.find((f) => f.key === 'status')?.value as string[]) ?? []
            const nextType = (filters.find((f) => f.key === 'type')?.value as OrgTypeFilter[]) ?? []
            this.filterAccountStatuses(nextStatus)
            this.filterOrgType(nextType)
        }

        const tableHeader = (
            <Flex className="accounts-list-header" gap={8} justify="space-between" align="center">
                <Flex flex={1} gap={8} align="center">
                    <Input.Search
                        className="accounts-list-search"
                        style={{ width: 300 }}
                        size="middle"
                        placeholder="Search by ID or Organization Name"
                        enterButton={true}
                        onSearch={this.searchAccounts}
                    />
                    <FilterBar
                        filterDefinitions={filterDefinitions}
                        value={getFilterBarValue()}
                        onApply={handleFilterBarApply}
                        showInlineTags={false}
                        showInlineClearAll={false}
                        size="middle"
                    />
                </Flex>
            </Flex>
        )

        return (
            <div className={classnames('page', 'accounts')}>
                <div className={classnames('page-wrapper')}>
                    <PageHeader
                        title="Organizations"
                        onTitleSet={noop}
                        action={
                            <CurrentUserCan do={AbilityAction.CREATE} on={SubjectEntity.ORG}>
                                <Button type="primary" onClick={this.showNewOrgPrompt} actions={newOrgActions}>
                                    New Organization
                                </Button>
                            </CurrentUserCan>
                        }
                    />

                    <Table<AccountTableDataDto>
                        className="aqe-table-well"
                        title={() => tableHeader}
                        size="small"
                        bordered={false}
                        dataSource={this.state.visibleAccounts}
                        rowKey={(row) => row.id}
                        onChange={this.handleChange}
                        pagination={false}
                        footer={(currentTableData) => (
                            <SwSimpleLinkPagination
                                currentTableData={currentTableData}
                                links={this.state.paginationLinks}
                                onPrev={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchAccounts())
                                }}
                                onNext={(_ev, _link, config) => {
                                    this.setState({ page: config.current! }, () => this.fetchAccounts())
                                }}
                            />
                        )}
                        loading={
                            this.isLoading && {
                                indicator: <Loading3QuartersOutlined spin={true} />,
                            }
                        }
                    >
                        <Table.Column className="id" title="ID" dataIndex="id" />
                        <Table.Column
                            className="account"
                            title="Organization"
                            dataIndex="display_name"
                            render={this.renderDisplay}
                        />
                        <Table.Column
                            className="created_at"
                            title="Created"
                            dataIndex="created_at"
                            render={this.renderCreatedAt}
                        />
                        <Table.Column
                            className="actions"
                            title="Actions"
                            align="center"
                            render={(_, account: AccountTableDataDto) => this.renderRowActions(account)}
                        />
                    </Table>

                    {/* Standard New Organization modal */}
                    <Modal
                        className="new-org-modal"
                        title="New Organization"
                        open={this.state.showNewAccountPrompt}
                        onOk={this.handleCreateNewAccount}
                        onCancel={this.hideNewOrgPrompt}
                    >
                        <Form ref={this.newAccountFormRef}>
                            <Form.Item
                                name="name"
                                label="Name"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value) {
                                                return Promise.reject(new Error('Organization name is required.'))
                                            } else {
                                                this.newAccountFormRef.current.setFieldsValue({ name: value.trim() })
                                                return Promise.resolve()
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Input data-1p-ignore={true} />
                            </Form.Item>

                            <Form.Item
                                name="platformWhitelabelId"
                                label="Platform Whitelabel"
                                initialValue={this.state.whiteLabels
                                    ?.find((val) => val.getIsDefault() === true)
                                    ?.getId()}
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value) {
                                                return Promise.reject(
                                                    new Error('Organization platform whitelabel is required.'),
                                                )
                                            } else {
                                                this.newAccountFormRef.current.setFieldsValue({
                                                    platformWhitelabel: value,
                                                })
                                                return Promise.resolve()
                                            }
                                        },
                                    },
                                ]}
                            >
                                <Select>
                                    {this.state.whiteLabels?.map((whiteLabel: WhiteLabelModel) => {
                                        return (
                                            <Select.Option key={whiteLabel.getId()} value={whiteLabel.getId()}>
                                                {whiteLabel.getDisplayName()}
                                            </Select.Option>
                                        )
                                    })}
                                </Select>
                            </Form.Item>
                        </Form>
                    </Modal>

                    {/* New Demo Organization modal — internal users only */}
                    <Modal
                        className="new-demo-org-modal"
                        title="New Demo Organization"
                        open={this.state.showNewDemoOrgPrompt}
                        onOk={this.handleCreateNewDemoOrg}
                        onCancel={this.hideNewDemoOrgPrompt}
                        okText="Create Demo Organization"
                        confirmLoading={this.state.showNewDemoOrgLoading}
                    >
                        <Form ref={this.newDemoOrgFormRef}>
                            <Form.Item
                                name="name"
                                label="Organization Name"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value?.trim()) {
                                                return Promise.reject(new Error('Organization name is required.'))
                                            }
                                            this.newDemoOrgFormRef.current.setFieldsValue({ name: value.trim() })
                                            return Promise.resolve()
                                        },
                                    },
                                ]}
                            >
                                <Input data-1p-ignore={true} />
                            </Form.Item>
                        </Form>
                    </Modal>

                    {/* New Demo Template Organization modal — internal users only */}
                    <Modal
                        className="new-demo-template-org-modal"
                        title="New Demo Template Organization"
                        open={this.state.showNewDemoTemplateOrgPrompt}
                        onOk={this.handleCreateNewDemoTemplateOrg}
                        onCancel={this.hideNewDemoTemplateOrgPrompt}
                        okText="Create Demo Template Organization"
                        confirmLoading={this.state.showNewDemoTemplateOrgLoading}
                    >
                        <Form ref={this.newDemoTemplateOrgFormRef}>
                            <Form.Item
                                name="name"
                                label="Organization Name"
                                validateTrigger="onSubmit"
                                rules={[
                                    {
                                        validator: (_: RuleObject, value) => {
                                            if (!value?.trim()) {
                                                return Promise.reject(new Error('Organization name is required.'))
                                            }
                                            this.newDemoTemplateOrgFormRef.current.setFieldsValue({ name: value.trim() })
                                            return Promise.resolve()
                                        },
                                    },
                                ]}
                            >
                                <Input data-1p-ignore={true} />
                            </Form.Item>
                        </Form>
                    </Modal>
                </div>
            </div>
        )
    }

    protected get isLoading(): boolean {
        return this.state.loading && !this.state.accountsLoaded
    }

    protected handleDateSort = (a: AccountTableDataDto, b: AccountTableDataDto): number => {
        const aCreated = moment(a.created_at)
        const bCreated = moment(b.created_at)

        return aCreated > bCreated ? 1 : aCreated < bCreated ? -1 : 0
    }

    protected renderDisplay = (_text: string, account: AccountTableDataDto): React.ReactNode => {
        const name = account.name

        const badge = account.is_demo_template
            ? <Tag color="orange" style={{ marginLeft: 6, verticalAlign: 'middle' }}>Template</Tag>
            : account.is_demo
            ? <Tag color="blue" style={{ marginLeft: 6, verticalAlign: 'middle' }}>Demo</Tag>
            : undefined

        return (
            <TableRowEntityDisplay
                title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                        <Link to={this.buildOrgUrl(account)}>{name}</Link>
                        {badge}
                    </span>
                }
                status={
                    <StatusBadge type="account" status={account.status as StatusBadgeProps['status']} expanded={true} />
                }
            />
        )
    }

    protected renderStatus = (_text: string, account: AccountTableDataDto): React.ReactNode => {
        let value: any = ''

        if (account.status === 'ACTIVE') {
            value = <Tag color="green">Active</Tag>
        } else {
            value = <Tag color="red">Disabled</Tag>
        }

        return value
    }

    protected renderCreatedAt = (text: string, _account: AccountTableDataDto): React.ReactNode => {
        return moment(text).format('YYYY/MM/DD')
    }

    protected renderRowActions(account: AccountTableDataDto): React.ReactNode {
        const actions: ButtonAction[] = []

        if (account.status === 'ACTIVE') {
            actions.push({
                key: 'disable',
                text: 'Disable',
                icon: <EyeOutlined />,
                onClick: () => this.handleActionClick('disable', account, null as any),
            })
        }

        if (account.status === 'DISABLED') {
            actions.push({
                key: 'enable',
                text: 'Enable',
                icon: <EyeOutlined />,
                onClick: () => this.handleActionClick('enable', account, null as any),
            })
        }

        return (
            <Button
                size="small"
                altHref={this.buildOrgUrl(account)}
                onClick={(ev) => this.handlePrimaryActionClick(account, ev as React.MouseEvent<HTMLElement>)}
                actions={actions.length > 0 ? actions : undefined}
            >
                View
            </Button>
        )
    }

    protected buildOrgUrl(org: AccountDto): string {
        return `/organizations/${org.id}`
    }

    protected jumpToOrg(org: AccountDto): void {
        this.appService.route(this.buildOrgUrl(org))
    }

    protected showNewOrgPrompt = () => {
        this.setState({ showNewAccountPrompt: true })
    }

    protected hideNewOrgPrompt = () => {
        this.newAccountFormRef.current?.setFieldsValue?.({ name: undefined })
        this.setState({ showNewAccountPrompt: false })
    }

    protected showNewDemoOrgPrompt = () => {
        this.setState({ showNewDemoOrgPrompt: true })
    }

    protected hideNewDemoOrgPrompt = () => {
        this.newDemoOrgFormRef.current?.resetFields?.()
        this.setState({ showNewDemoOrgPrompt: false })
    }

    protected showNewDemoTemplateOrgPrompt = () => {
        this.setState({ showNewDemoTemplateOrgPrompt: true })
    }

    protected hideNewDemoTemplateOrgPrompt = () => {
        this.newDemoTemplateOrgFormRef.current?.resetFields?.()
        this.setState({ showNewDemoTemplateOrgPrompt: false })
    }

    protected handleCreateNewAccount = async (): Promise<void> => {
        const showError = () =>
            simpleNotification('error', 'Cannot process new organization at this time. Please try again later.')

        if (this.newAccountFormRef.current) {
            try {
                const values = await this.newAccountFormRef.current.validateFields()

                const { ok, data } = await this.orgService.create({
                    name: values.name,
                    platformWhitelabelId: values.platformWhitelabelId,
                })

                if (ok) {
                    this.hideNewOrgPrompt()
                    await this.appState.abilityStore.refresh()
                    this.jumpToOrg(data)
                }
            } catch (_) {}
        } else {
            showError()
        }
    }

    protected handleCreateNewDemoOrg = async (): Promise<void> => {
        if (!this.newDemoOrgFormRef.current) {
            simpleNotification('error', 'Cannot process new demo organization at this time. Please try again later.')
            return
        }

        try {
            const values = await this.newDemoOrgFormRef.current.validateFields()
            this.setState({ showNewDemoOrgLoading: true })

            const defaultWhitelabelId = this.state.whiteLabels?.find((wl) => wl.getIsDefault())?.getId()

            const { ok, data } = await this.orgService.create({
                name: values.name,
                isDemo: true,
                platformWhitelabelId: defaultWhitelabelId,
            }, false)

            if (ok) {
                this.hideNewDemoOrgPrompt()
                await this.appState.abilityStore.refresh()
                this.jumpToOrg(data)
            }
        } catch (_) {
        } finally {
            this.setState({ showNewDemoOrgLoading: false })
        }
    }

    protected handleCreateNewDemoTemplateOrg = async (): Promise<void> => {
        if (!this.newDemoTemplateOrgFormRef.current) {
            simpleNotification('error', 'Cannot process new demo template organization at this time. Please try again later.')
            return
        }

        try {
            const values = await this.newDemoTemplateOrgFormRef.current.validateFields()
            this.setState({ showNewDemoTemplateOrgLoading: true })

            const defaultWhitelabelId = this.state.whiteLabels?.find((wl) => wl.getIsDefault())?.getId()

            const { ok, data } = await this.orgService.create({
                name: values.name,
                isDemoTemplate: true,
                platformWhitelabelId: defaultWhitelabelId,
            }, false)

            if (ok) {
                this.hideNewDemoTemplateOrgPrompt()
                await this.appState.abilityStore.refresh()
                this.jumpToOrg(data)
            }
        } catch (_) {
        } finally {
            this.setState({ showNewDemoTemplateOrgLoading: false })
        }
    }

    protected handleChange = (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter: SorterResult<AccountTableDataDto>,
        _extra: TableCurrentDataSource<AccountTableDataDto>,
    ) => {
        this.setState({ sortedInfo: sorter })
        this.fetchAccounts()
    }

    protected handlePrimaryActionClick(account: AccountTableDataDto, event: React.MouseEvent<HTMLElement>): void {
        if (event.metaKey) {
            window.open(this.buildOrgUrl(account))
        } else {
            event.preventDefault()
            this.appService.route(this.buildOrgUrl(account), { account })
        }
    }

    protected handleActionClick = (
        key: string | number,
        account: AccountTableDataDto,
        _domEvent: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    ): void => {
        if (key === 'disable') {
            const formRef = React.createRef<FormInstance>()

            Modal.confirm({
                title: 'Disable Org',
                icon: <WarningOutlined />,
                okText: 'Disable',
                onOk: async () => {
                    const form = formRef.current

                    if (!!form) {
                        await form.validateFields()
                        return this.handleDisable(account)
                    }
                },
                content: (
                    <Form ref={formRef}>
                        <p>
                            Are you sure you want to disable this organization? All associated domains will be disabled
                            as well.
                        </p>

                        <p>
                            Please type the Organization name, <b>{account.name}</b>, to continue with this action.
                        </p>

                        <Form.Item
                            name="name"
                            validateTrigger={false}
                            rules={[
                                {
                                    validator: (_rule: RuleObject, value, callback) => {
                                        if (!value?.trim()) {
                                            callback('Account name is required.')
                                        } else if (value !== account.name) {
                                            callback('Account name does not match.')
                                        } else {
                                            callback()
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input autoFocus={true} />
                        </Form.Item>
                    </Form>
                ),
            } as any)
        } else if (key === 'enable') {
            Modal.confirm({
                title: 'Enable Org',
                icon: <QuestionCircleOutlined />,
                okText: 'Enable',
                onOk: () => this.handleEnable(account),
                content: (
                    <p>
                        Are you sure you want to enable this organization? All associated domains will be enabled as
                        well. Individual domains can be disabled through the domains listing.
                    </p>
                ),
            } as any)
        }
    }

    protected handleDisable = async (account: AccountTableDataDto) => {
        await this.orgService.patch(account.id, {
            status: 'DISABLED',
        })

        return this.fetchAccounts()
    }

    protected handleEnable = async (account: AccountTableDataDto) => {
        await this.orgService.patch(account.id, {
            status: 'ACTIVE',
        })

        return this.fetchAccounts()
    }

    protected filterAccountStatuses = async (statusFilter: string[]) => {
        if (!deepEqual(statusFilter, this.state.statusFilter)) {
            await this.setState({ statusFilter })

            return this.fetchAccounts()
        }
    }

    protected filterOrgType = async (orgTypeFilter: OrgTypeFilter[]) => {
        if (!deepEqual(orgTypeFilter, this.state.orgTypeFilter)) {
            await this.setState({ orgTypeFilter, page: 1 })
            return this.fetchAccounts()
        }
    }

    protected searchAccounts = async (data: string | any) => {
        const value = typeof data !== 'string' ? data.target.value : data

        if (!value || value.length === 0 || value.length >= 2) {
            await this.setState({ page: 1, query: value })
            await this.fetchAccounts(value)
        }
    }

    protected async fetchAccounts(search?: any) {
        this.setState({ loading: true, accountsLoaded: false })
        let update: Partial<IAccountsState>

        // Build is_demo / is_demo_template query params from the org type filter.
        // When all three are selected (or none), no filter is applied.
        const { orgTypeFilter } = this.state
        const allSelected = orgTypeFilter.length === 0 || orgTypeFilter.length === 3
        const orgTypeQuery: Record<string, any> = {}

        if (!allSelected) {
            const hasStandard = orgTypeFilter.includes('standard')
            const hasDemo = orgTypeFilter.includes('demo')
            const hasTemplate = orgTypeFilter.includes('demo-template')

            if (hasTemplate && !hasStandard && !hasDemo) {
                // Template only
                orgTypeQuery.is_demo_template = true
            } else if (hasDemo && !hasStandard && !hasTemplate) {
                // Demo only (not templates)
                orgTypeQuery.is_demo = true
                orgTypeQuery.is_demo_template = false
            } else if (hasStandard && !hasDemo && !hasTemplate) {
                // Standard only
                orgTypeQuery.is_demo = false
            } else if (hasStandard && hasDemo && !hasTemplate) {
                // Standard + Demo (exclude templates)
                orgTypeQuery.is_demo_template = false
            } else if (hasDemo && hasTemplate && !hasStandard) {
                // Demo + Template (exclude standard)
                orgTypeQuery.is_demo = true
            }
            // Standard + Template: no clean single API param — fetch all and note this is an edge case
        }

        try {
            const { data, meta } =
                (await this.orgService.fetchAll({
                    query: {
                        limit: this.state.pageSize,
                        page: this.state.page,
                        search: search ? search : this.state.query,
                        pagination: true,
                        status: this.state.statusFilter.join(','),
                        ...orgTypeQuery,
                    },
                    showLoadingScreen: false,
                })) || {}

            const accountDtos: AccountDto[] = data.map((account: AccountDto) => {
                return AccountTableDataDto.fromAccountDto(account)
            })

            update = {
                accountsLoaded: true,
                allAccounts: accountDtos,
                visibleAccounts: accountDtos,
                accountsMeta: meta,
                paginationLinks: meta.links ?? this.state.paginationLinks,
            }
        } catch (error) {
            console.warn(error)
        }

        this.setState({
            loading: false,
            ...(update as any),
        })
    }

    protected async fetchWhitelabels() {
        let whiteLabels
        const res = await fetchWhiteLabels({
            showLoadingScreen: false,
        })

        if (res?.ok) {
            whiteLabels = res.data?.map(WhiteLabelModel.build) ?? []
        }

        this.setState({ whiteLabels })
    }
}
